import type { AppRouter } from "@immi/api"
import { QueryCache, QueryClient } from "@tanstack/react-query"
import {
  createTRPCClient,
  httpBatchLink,
  httpBatchStreamLink,
  splitLink,
} from "@trpc/client"
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query"
import { toast } from "sonner"

import { queryErrorToastMessage } from "./error-toast"

function makeQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.suppressErrorToast) return
        toast.error(queryErrorToastMessage(error), {
          action: {
            label: "Tentar novamente",
            onClick: () => {
              query.invalidate()
            },
          },
        })
      },
    }),
  })
}

// Client components also render on the SERVER: a module-scoped QueryClient
// would be shared across requests (and across accounts). A singleton only in
// the browser; on the server, a new instance per request.
let browserQueryClient: QueryClient | undefined

export function getQueryClient(): QueryClient {
  if (typeof window === "undefined") return makeQueryClient()
  browserQueryClient ??= makeQueryClient()
  return browserQueryClient
}

/**
 * Compatibility: code that already imported `queryClient` keeps working, but
 * it now resolves the right client on each access instead of pinning the
 * module's. In new code prefer `useQueryClient()` (or `getQueryClient()`).
 */
export const queryClient: QueryClient = new Proxy({} as QueryClient, {
  get(_target, prop) {
    const client = getQueryClient()
    const value = Reflect.get(client, prop, client)
    return typeof value === "function" ? value.bind(client) : value
  },
})

// Procedures that return a generator need the streaming link, and only they
// get it: a stream sends its headers before the body, and the rest of the app
// stays on the usual batch link. A generator left out of this list fails with
// 415 before it runs.
const STREAMING_PATHS = new Set([
  "postlab.review.create",
  "postlab.review.turnStream",
  "postlab.posts.draftStream",
  "lessonTutor.ask",
])

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    splitLink({
      condition: (op) => STREAMING_PATHS.has(op.path),
      true: httpBatchStreamLink({ url: "/api/trpc" }),
      false: httpBatchLink({ url: "/api/trpc" }),
    }),
  ],
})

export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient: getQueryClient,
})
